// Records a voice note with the browser's MediaRecorder, always as AAC in an
// MP4 container: the one format every client plays (iOS cannot decode the
// WebM/Opus most browsers default to). A browser that can't record MP4 gets
// `supported: false` and the Composer disables the microphone; it can still
// listen.

import { useCallback, useEffect, useRef, useState } from 'react';
import { createLevelMeter, type ILevelMeter } from './micLevel';

export const VOICE_MAX_MS = 3 * 60 * 1000; // 3 minutes
export const VOICE_MIN_MS = 1000; // shorter is an accidental tap, not a message
export const TOO_SHORT_NOTICE_MS = 5000;
// Codec named: a bare audio/mp4 can record Opus, which iOS can't play.
export const VOICE_MIME = 'audio/mp4;codecs=mp4a.40.2';

export interface VoiceRecording {
  blob: Blob;
  url: string;
  durationMs: number;
  mime: string;
}

interface IUseVoiceRecorder {
  /** Whether this browser can record the MP4 the app requires. */
  supported: boolean;
  recording: boolean;
  elapsedMs: number;
  error: string | null;
  tooShort: boolean;
  start: (onAutoStop?: (rec: VoiceRecording) => void) => Promise<void>;
  stop: () => Promise<VoiceRecording | null>;
  cancel: () => void;
  /** Hides the "too short" notice (e.g. once something else is sent). */
  dismissTooShort: () => void;
  /** Mic loudness right now, 0..1; 0 when not recording. */
  getLevel: () => number;
}

export function canRecordVoice(): boolean {
  return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(VOICE_MIME);
}

function collect(recorder: MediaRecorder, chunks: Blob[], startedAt: number): VoiceRecording {
  const mime = recorder.mimeType || VOICE_MIME;
  const blob = new Blob(chunks, { type: mime });
  return { blob, url: URL.createObjectURL(blob), durationMs: Date.now() - startedAt, mime };
}

export function useVoiceRecorder(): IUseVoiceRecorder {
  const [supported] = useState(canRecordVoice);
  const [recording, setRecording] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [tooShort, setTooShort] = useState(false);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number>(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const maxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const meterRef = useRef<ILevelMeter | null>(null);

  const cleanupStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    meterRef.current?.close();
    meterRef.current = null;
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (maxTimerRef.current) {
      clearTimeout(maxTimerRef.current);
      maxTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => cleanupStream(), [cleanupStream]);

  useEffect(() => {
    if (!tooShort) return;
    const id = setTimeout(() => setTooShort(false), TOO_SHORT_NOTICE_MS);
    return () => clearTimeout(id);
  }, [tooShort]);

  const start = useCallback(async (onAutoStop?: (rec: VoiceRecording) => void) => {
    setError(null);
    setTooShort(false);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      meterRef.current = createLevelMeter(stream);
      const recorder = new MediaRecorder(stream, { mimeType: VOICE_MIME });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.start();
      recorderRef.current = recorder;
      startedAtRef.current = Date.now();
      setElapsedMs(0);
      setRecording(true);
      tickRef.current = setInterval(() => {
        setElapsedMs(Date.now() - startedAtRef.current);
      }, 200);
      maxTimerRef.current = setTimeout(() => {
        // Auto-stop at the max duration limit. Build the recording inline the
        // same way stop() does, so the Composer's onAutoStop handler receives it.
        const rec = recorderRef.current;
        if (!rec || rec.state === 'inactive') return;
        rec.onstop = () => onAutoStop?.(collect(rec, chunksRef.current, startedAtRef.current));
        rec.stop();
        setRecording(false);
        cleanupStream();
        recorderRef.current = null;
      }, VOICE_MAX_MS);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      cleanupStream();
    }
  }, [cleanupStream]);

  const stop = useCallback(async (): Promise<VoiceRecording | null> => {
    const recorder = recorderRef.current;
    if (!recorder) return null;

    const done = new Promise<VoiceRecording>((resolve) => {
      recorder.onstop = () => resolve(collect(recorder, chunksRef.current, startedAtRef.current));
    });

    recorder.stop();
    setRecording(false);
    cleanupStream();
    const result = await done;
    recorderRef.current = null;
    if (result.durationMs < VOICE_MIN_MS) {
      URL.revokeObjectURL(result.url);
      setTooShort(true);
      return null;
    }
    return result;
  }, [cleanupStream]);

  const cancel = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = null;
      recorder.stop();
    }
    recorderRef.current = null;
    chunksRef.current = [];
    cleanupStream();
    setRecording(false);
    setElapsedMs(0);
  }, [cleanupStream]);

  const dismissTooShort = useCallback(() => setTooShort(false), []);

  const getLevel = useCallback(() => meterRef.current?.read() ?? 0, []);

  return { supported, recording, elapsedMs, error, tooShort, start, stop, cancel, dismissTooShort, getLevel };
}
